/**
 * SyncProductsModal — F18 T18.5.
 *
 * Modal dwuetapowy (kopia wzorca SyncContractorsModal):
 *  1. Wybór trybu — full (pełen sync) / incremental (od daty).
 *  2. Progress — polling job status + stats `{added, updated, unchanged, skipped}`.
 *
 * POST /api/erp-integrations/:id/sync-products. Timeout UI 5 min.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AlertCircle, CheckCircle2, Clock, Loader2 } from 'lucide-react';

import { Button } from '@/components/Button';
import { Modal } from '@/components/Modal';
import { api } from '@/lib/api';

import { isJobInFlight } from './job-in-flight';
import type { JobDto } from './TestConnectionModal';

type Stage = 'choose' | 'progress';
type Mode = 'full' | 'incremental';

interface Props {
  open: boolean;
  integrationId: string | null;
  integrationName: string;
  onClose: () => void;
}

const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 5 * 60_000;

interface SyncStats {
  added?: number;
  updated?: number;
  unchanged?: number;
  skipped?: number;
}

interface SyncResultPayload {
  outcome?: 'ok' | 'partial' | 'no_op';
  stats?: SyncStats;
  warnings?: string[];
}

function todayISO(): string {
  return new Date().toISOString().slice(0, 10);
}

function dateMinus30ISO(): string {
  const d = new Date();
  d.setDate(d.getDate() - 30);
  return d.toISOString().slice(0, 10);
}

export function SyncProductsModal({ open, integrationId, integrationName, onClose }: Props) {
  const { t } = useTranslation();
  const [stage, setStage] = useState<Stage>('choose');
  const [mode, setMode] = useState<Mode>('full');
  const [dateFrom, setDateFrom] = useState<string>(dateMinus30ISO());
  const [jobId, setJobId] = useState<string | null>(null);
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    if (!open) {
      setStage('choose');
      setMode('full');
      setJobId(null);
      setTimedOut(false);
      return;
    }
    if (stage !== 'progress' || !jobId) return;
    const timer = window.setTimeout(() => setTimedOut(true), POLL_TIMEOUT_MS);
    return () => window.clearTimeout(timer);
  }, [open, stage, jobId]);

  const startSyncMutation = useMutation({
    mutationFn: async () => {
      if (!integrationId) throw new Error('No integrationId');
      const body: { mode: Mode; dateFrom?: string } = { mode };
      if (mode === 'incremental') body.dateFrom = dateFrom;
      const res = await api.post<{ jobId: string }>(
        `/erp-integrations/${integrationId}/sync-products`,
        body,
      );
      return res.data.jobId;
    },
    onSuccess: (id) => {
      setJobId(id);
      setStage('progress');
    },
  });

  const jobQ = useQuery({
    queryKey: ['erp-sync-job', jobId],
    queryFn: async () => {
      const res = await api.get<{ job: JobDto }>(`/erp-sync-jobs/${jobId}`);
      return res.data.job;
    },
    enabled: !!jobId && open && !timedOut && stage === 'progress',
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (status === 'completed' || status === 'failed') return false;
      return POLL_INTERVAL_MS;
    },
  });

  if (!open) return null;

  const job = jobQ.data ?? null;
  const result = (job?.result ?? null) as SyncResultPayload | null;
  // Wysyłanie żądania albo zadanie w toku — tło i Esc nie zamykają okna (X i „Zamknij" działają).
  const running =
    startSyncMutation.isPending ||
    (stage === 'progress' && isJobInFlight(job, timedOut, jobQ.isError));

  return (
    <Modal
      open
      onClose={onClose}
      title={t('tenant.settings.integrations.syncProducts.title', { name: integrationName })}
      size="md"
      testId="sync-products-modal"
      closeOnBackdrop={!running}
      closeOnEsc={!running}
      footer={
        <>
          {stage === 'choose' && (
            <>
              <Button variant="secondary" onClick={onClose}>
                {t('common.cancel')}
              </Button>
              <Button
                variant="primary"
                onClick={() => startSyncMutation.mutate()}
                disabled={startSyncMutation.isPending || (mode === 'incremental' && !dateFrom)}
                data-testid="sync-products-start-btn"
              >
                {startSyncMutation.isPending
                  ? t('tenant.settings.integrations.syncProducts.starting')
                  : t('tenant.settings.integrations.syncProducts.startButton')}
              </Button>
            </>
          )}
          {stage === 'progress' && (
            <Button variant="primary" onClick={onClose} data-testid="sync-products-close-btn">
              {t('common.close')}
            </Button>
          )}
        </>
      }
    >
      {stage === 'choose' && (
        <div className="space-y-4" data-testid="sync-products-stage-choose">
          <p className="text-sm text-text-secondary">
            {t('tenant.settings.integrations.syncProducts.chooseHint')}
          </p>
          <fieldset className="space-y-2">
            <legend className="sr-only">
              {t('tenant.settings.integrations.syncProducts.modeLegend')}
            </legend>
            <label className="flex cursor-pointer items-start gap-3 rounded-md border border-border-primary p-3 hover:bg-bg-secondary">
              <input
                type="radio"
                name="sync-products-mode"
                value="full"
                checked={mode === 'full'}
                onChange={() => setMode('full')}
                className="mt-1"
                data-testid="sync-products-mode-full"
              />
              <div>
                <div className="font-medium text-text-primary">
                  {t('tenant.settings.integrations.syncProducts.modeFullTitle')}
                </div>
                <div className="text-xs text-text-tertiary">
                  {t('tenant.settings.integrations.syncProducts.modeFullHint')}
                </div>
              </div>
            </label>
            <label className="flex cursor-pointer items-start gap-3 rounded-md border border-border-primary p-3 hover:bg-bg-secondary">
              <input
                type="radio"
                name="sync-products-mode"
                value="incremental"
                checked={mode === 'incremental'}
                onChange={() => setMode('incremental')}
                className="mt-1"
                data-testid="sync-products-mode-incremental"
              />
              <div className="flex-1">
                <div className="font-medium text-text-primary">
                  {t('tenant.settings.integrations.syncProducts.modeIncrementalTitle')}
                </div>
                <div className="text-xs text-text-tertiary">
                  {t('tenant.settings.integrations.syncProducts.modeIncrementalHint')}
                </div>
                {mode === 'incremental' && (
                  <div className="mt-2">
                    <label className="block text-xs font-medium text-text-secondary">
                      {t('tenant.settings.integrations.syncProducts.dateFromLabel')}
                    </label>
                    <input
                      type="date"
                      value={dateFrom}
                      max={todayISO()}
                      onChange={(e) => setDateFrom(e.target.value)}
                      className="mt-1 w-full rounded-md border border-border-primary bg-bg-primary px-2 py-1 text-sm"
                      data-testid="sync-products-date-from"
                    />
                  </div>
                )}
              </div>
            </label>
          </fieldset>

          {startSyncMutation.isError && (
            <div className="rounded-md border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-700 dark:text-red-400">
              {t('tenant.settings.integrations.syncProducts.startError')}
            </div>
          )}
        </div>
      )}

      {stage === 'progress' && (
        <div
          className="space-y-4"
          data-testid={`sync-products-status-${
            job?.status === 'completed' || job?.status === 'failed'
              ? job.status
              : timedOut
                ? 'timeout'
                : (job?.status ?? 'pending')
          }`}
        >
          {!timedOut && (!job || job.status === 'pending' || job.status === 'running') && (
            <div className="flex items-center gap-3 rounded-md bg-bg-secondary p-3">
              <Loader2 className="h-5 w-5 animate-spin text-accent-500" aria-hidden="true" />
              <div>
                <div className="font-medium text-text-primary">
                  {job?.status === 'running'
                    ? t('tenant.settings.integrations.syncProducts.running')
                    : t('tenant.settings.integrations.syncProducts.waitingForAgent')}
                </div>
                <div className="text-xs text-text-tertiary">
                  {t('tenant.settings.integrations.syncProducts.runningHint')}
                </div>
              </div>
            </div>
          )}

          {timedOut && job?.status !== 'completed' && job?.status !== 'failed' && (
            <div className="flex items-start gap-3 rounded-md border border-red-500/30 bg-red-500/10 p-3">
              <Clock className="mt-0.5 h-5 w-5 text-red-600 dark:text-red-400" aria-hidden="true" />
              <div>
                <div className="font-medium text-red-600 dark:text-red-400">
                  {t('tenant.settings.integrations.syncProducts.timeoutTitle')}
                </div>
                <div className="text-xs text-text-secondary">
                  {t('tenant.settings.integrations.syncProducts.timeoutHint')}
                </div>
              </div>
            </div>
          )}

          {job?.status === 'completed' && (
            <div className="space-y-3">
              <div className="flex items-start gap-3 rounded-md border border-green-500/30 bg-green-500/10 p-3">
                <CheckCircle2 className="mt-0.5 h-5 w-5 text-green-600" aria-hidden="true" />
                <div>
                  <div className="font-medium text-green-700 dark:text-green-400">
                    {t('tenant.settings.integrations.syncProducts.successTitle')}
                  </div>
                  <div className="text-xs text-text-secondary">
                    {t('tenant.settings.integrations.syncProducts.successHint')}
                  </div>
                </div>
              </div>

              <StatsGrid stats={result?.stats ?? {}} />

              {result?.warnings && result.warnings.length > 0 && (
                <details className="rounded-md border border-yellow-500/30 bg-yellow-500/5 p-3">
                  <summary className="cursor-pointer text-xs font-medium text-yellow-700 dark:text-yellow-400">
                    {t('tenant.settings.integrations.syncProducts.warningsLabel', {
                      count: result.warnings.length,
                    })}
                  </summary>
                  <ul className="mt-2 space-y-1 text-xs text-text-secondary">
                    {result.warnings.slice(0, 20).map((w, i) => (
                      <li key={i} className="font-mono">
                        • {w}
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          )}

          {job?.status === 'failed' && (
            <div className="space-y-3">
              <div className="flex items-start gap-3 rounded-md border border-red-500/30 bg-red-500/10 p-3">
                <AlertCircle
                  className="mt-0.5 h-5 w-5 text-red-600 dark:text-red-400"
                  aria-hidden="true"
                />
                <div>
                  <div className="font-medium text-red-600 dark:text-red-400">
                    {t('tenant.settings.integrations.syncProducts.failedTitle')}
                  </div>
                  <div className="text-xs text-text-secondary">
                    {t('tenant.settings.integrations.syncProducts.failedHint')}
                  </div>
                </div>
              </div>

              {job.result && (
                <pre className="max-h-64 overflow-auto rounded-md border border-red-500/30 bg-red-500/5 p-3 font-mono text-xs text-red-700 dark:text-red-400">
                  {JSON.stringify(job.result, null, 2)}
                </pre>
              )}
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}

function StatsGrid({ stats }: { stats: SyncStats }) {
  const { t } = useTranslation();
  const cells: Array<[string, number | undefined, string]> = [
    [t('tenant.settings.integrations.syncProducts.stats.added'), stats.added, 'text-green-600'],
    [
      t('tenant.settings.integrations.syncProducts.stats.updated'),
      stats.updated,
      'text-accent-500',
    ],
    [
      t('tenant.settings.integrations.syncProducts.stats.unchanged'),
      stats.unchanged,
      'text-text-tertiary',
    ],
    [
      t('tenant.settings.integrations.syncProducts.stats.skipped'),
      stats.skipped,
      'text-yellow-600',
    ],
  ];
  return (
    <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
      {cells.map(([label, val, color]) => (
        <div
          key={label}
          className="rounded-md border border-border-primary bg-bg-secondary p-2 text-center"
        >
          <div className={`text-xl font-bold ${color}`}>{val ?? 0}</div>
          <div className="text-xs text-text-tertiary">{label}</div>
        </div>
      ))}
    </div>
  );
}
